import sanitizeHtml from "sanitize-html";

const SANITIZE_OPTIONS = Object.freeze({
    allowedTags: ["b", "i", "em", "strong", "ul", "ol", "li"],
    allowedAttributes: {},
    allowedIframeHostnames: []
});

const SANITIZE_EMPTY_OPTIONS = Object.freeze({
    allowedTags: [],
    allowedAttributes: {},
    allowedIframeHostnames: []
});

/** 🔹 Internal helper: filter by property and optional additional pairs */
function filterProp(collection = [], propName, propValue, otherNameValues) {
    return collection.filter(obj =>
        obj[propName] === propValue &&
        (!otherNameValues ||
            otherNameValues.every((v, i) =>
                i % 2 === 0 ? obj[otherNameValues[i]] === otherNameValues[i + 1] : true
            ))
    );
}

/** 🔹 Safe coalesce returning first non-nullish value */
export function coalesce(...args) {
    for (const v of args) if (v != null) return v;
    return undefined;
}

/** 🔹 Safe HTML sanitizers */
export const sanitize = html => ({ __html: sanitizeHtml(html ?? "", SANITIZE_OPTIONS) });
export const sanitizeValue = html => sanitizeHtml(html ?? "", SANITIZE_OPTIONS);
export const sanitizeNoAllowedTags = html =>
    sanitizeHtml(html ?? "", SANITIZE_EMPTY_OPTIONS);

/** Convert Quill 2 list markup into semantic HTML for non-Quill renderers. */
export const normalizeQuillLists = html => {
    const container = document.createElement("div");
    container.innerHTML = html ?? "";

    container.querySelectorAll("ol, ul").forEach(list => {
        const hasQuillItems = Array.from(list.children).some(
            child => child.tagName === "LI" && child.hasAttribute("data-list")
        );
        if (!hasQuillItems) return;

        const fragment = document.createDocumentFragment();
        let normalizedList = null;
        let normalizedType = null;

        Array.from(list.children).forEach(child => {
            if (child.tagName !== "LI") {
                fragment.appendChild(child);
                normalizedList = null;
                normalizedType = null;
                return;
            }

            const listType = child.getAttribute("data-list") === "bullet" ? "ul" : "ol";
            if (!normalizedList || normalizedType !== listType) {
                normalizedList = document.createElement(listType);
                normalizedType = listType;
                fragment.appendChild(normalizedList);
            }

            child.removeAttribute("data-list");
            normalizedList.appendChild(child);
        });

        list.replaceWith(fragment);
    });

    return container.innerHTML;
};

/** 🔹 Year-to-timestamp helpers */
export const getStartTimeFromYear = year =>
    Date.UTC(parseInt(year, 10), 0, 1, 0, 0, 0, 0);

export const getEndTimeFromYear = year =>
    Date.UTC(parseInt(year, 10), 11, 31, 23, 59, 59, 999);

export const getFullYearFromString = date =>
    new Date(date).getUTCFullYear();

/** 🔹 Generic object utilities */
export default class ObjectUtils {
    /** Find first matching object by one or more properties */
    static findByObjectProp(collection, propName, propValue, ...otherNameValues) {
        // ✅ Early exit if input invalid
        if (!Array.isArray(collection) || !propName) return {};

        // ✅ Apply filtering logic safely
        const filtered = filterProp(collection, propName, propValue, ...otherNameValues);

        // ✅ Handle weird returns (null, undefined, not array)
        if (!Array.isArray(filtered) || filtered.length === 0) return {};

        // ✅ Return first match
        return filtered[0];
    }

    static findArrayByObjectProp(collection, propName, propValue, ...otherNameValues) {
        // ✅ Early exit if input invalid
        if (!Array.isArray(collection) || !propName) return [];

        // ✅ Apply filtering logic safely
        const filtered = filterProp(collection, propName, propValue, ...otherNameValues);

        // ✅ Handle weird returns (null, undefined, not array)
        if (!Array.isArray(filtered) || filtered.length === 0) return [];

        // ✅ Return first match
        return filtered;
    }

    /** Truncate string safely */
    static trunc(value, max = 0) {
        const str = String(value ?? "");
        return str.length > max ? `${str.slice(0, max - 1)}…` : str;
    }

    static toKebabCase(str) {
        return str
            ?.replace(/([a-z])([A-Z])/g, "$1-$2")
            .replace(/[\s_]+/g, "-")
            .toLowerCase()
            .trim();
    }

    static findObjectIndexInArray(collection = [], propName, propValue) {
        return collection.findIndex(obj => obj?.[propName] === propValue);
    }

    static removeObjectByProp(collection = [], propName, propValue) {
        return collection.filter(o => o?.[propName] !== propValue);
    }

    /** Check if nested property path exists */
    static hasProperty(root, ...props) {
        return !!props.reduce(
            (acc, key) => (acc && key in acc ? acc[key] : undefined),
            root
        );
    }

    /** Get nested property value or null */
    static getDeepProperty(root, ...props) {
        return props.reduce((acc, key) => (acc && key in acc ? acc[key] : null), root);
    }

    /** Deep clone using structuredClone if available */
    static deepClone(obj) {
        if (typeof structuredClone === "function") {
            try {
                return structuredClone(obj);
            } catch {
                // fallback
            }
        }
        return JSON.parse(JSON.stringify(obj));
    }

    /** Create index mapping property values to array indexes */
    static indexByProperty(collection = [], propertyName) {
        return collection.reduce((acc, curr, i) => {
            const key = curr?.[propertyName];
            if (key != null) {
                acc[key] ??= { indexes: [] };
                acc[key].indexes.push(i);
            }
            return acc;
        }, {});
    }

    /** Compare elements against a template */
    static findByTemplate(collection = [], template = {}) {
        return collection.filter(el =>
            Object.entries(template).every(([k, v]) =>
                v == null ? true : coalesce(el[k], null) === coalesce(v, null)
            )
        );
    }

    /** Exclude elements matching a template */
    static filterExcludeByTemplate(collection = [], template = {}, excludeProps = []) {
        return collection.filter(el =>
            !Object.entries(template).every(([k, v]) =>
                excludeProps.includes(k) ? true : v == null ? true : coalesce(el[k], null) === coalesce(v, null)
            )
        );
    }

    /** Safe optional getter returning {} instead of nullish */
    static opt(value) {
        return value ?? {};
    }
}
